import { useState, useEffect, useRef } from 'react'
import { ctx } from '../outline/ctx.js'
import { wf } from '../outline/state.js'
import { COMMANDS } from '../outline/commandRegistry.js'
import { executeCommand, selectPickerItem } from '../outline/commandExecutor.js'
import { getActiveProjects } from '../services/commands.js'

/* ── Role filter ─────────────────────────────────────────────────────────────── */

function isVisible(cmd) {
  if (!cmd.roles) return true
  const role = ctx.currentMember?.fields?.['Member Type'] || ''
  return cmd.roles.includes(role)
}

/* ── Fuzzy project match ─────────────────────────────────────────────────────── */

/**
 * Returns true if all whitespace-separated tokens in `query` appear anywhere
 * in the project's number + name string.
 *
 * Examples:
 *   matchProject(proj, '24')      → matches all 24-xx projects
 *   matchProject(proj, 'agan')    → matches any project whose name contains "agan"
 *   matchProject(proj, '24 ag')   → number contains "24" AND name contains "ag"
 */
function matchProject(proj, query) {
  if (!query) return true
  const tokens = query.toLowerCase().split(/\s+/).filter(Boolean)
  const hay = [
    proj.fields['Project Number'] || '',
    proj.fields['Project Name']   || '',
  ].join(' ').toLowerCase()
  return tokens.every(t => hay.includes(t))
}

/* ── Main component ──────────────────────────────────────────────────────────── */

export default function CommandBar() {
  const [open,        setOpen]        = useState(false)
  const [query,       setQuery]       = useState('')
  const [result,      setResult]      = useState(null)
  const [loading,     setLoading]     = useState(false)
  const [selectedIdx, setSelectedIdx] = useState(0)
  const [activeCmdId, setActiveCmdId] = useState(null)
  const [histIdx,     setHistIdx]     = useState(-1)   // -1 = not recalling history; ≥0 = index into wf.commandHistory

  // Project list — fetched once when the bar first opens, cached for the session.
  // useState so the component re-renders when projects arrive.
  const [projects,         setProjects]         = useState([])
  const projectsFetchedRef = useRef(false)   // prevents re-fetching across open/close

  const inputRef = useRef(null)

  /* ── Derived state ──────────────────────────────────────────────────────────── */

  const normalised = query.startsWith('/') ? query : '/' + query
  // Real text typed after the prompt slash. A bare '/' (e.g. after backspacing the input clear) is
  // NOT a query — without this guard every command matches (they all start with '/').
  const hasTyped = normalised.length > 1

  // Parse the command trigger and any argument text from the current input
  const inputParts = normalised.trim().split(/\s+/)
  const cmdTrigger = inputParts[0]                      // e.g. '/go'
  const argText    = inputParts.slice(1).join(' ')      // e.g. '24 ag'

  // Is the user typing an argument for a projectArg command?
  const activeCmd        = COMMANDS.find(c => c.trigger === cmdTrigger && isVisible(c))
  const isProjectArgMode = !!activeCmd?.projectArg

  // Live-filtered project list shown when in project-arg mode
  const projectSuggestions = isProjectArgMode
    ? projects
        .filter(p => matchProject(p, argText))
        .map(p => ({
          id:        p.id,
          label:     p.fields['Project Number'] || '',
          sublabel:  p.fields['Project Name'] || 'Unnamed',
          projectId: p.id,
        }))
    : []

  // Filtered command list shown while typing (before execution, non-project-arg mode).
  // hidden:true commands are excluded — they still run if typed explicitly.
  // Match only the command itself, as a prefix of what's typed (exact phrasing + letter order) —
  // not loose letter matches in the label/description. "/d" → /dark /drawings /debug, not /overdue.
  const filtered = (!result && hasTyped && !isProjectArgMode)
    ? COMMANDS.filter(cmd => !cmd.hidden && isVisible(cmd) && cmd.trigger.startsWith(normalised))
    : []

  // Suggestion list shown above the feedback line:
  //   - project-arg mode: live project list (overrides everything else)
  //   - picker:           selectable project/item list from executor result
  //   - help:             full command table
  //   - typing:           filtered commands
  const suggestions =
    isProjectArgMode      ? projectSuggestions :
    result?.type === 'picker' ? result.items :
    result?.type === 'help'   ? COMMANDS.filter(c => isVisible(c) && !c.hidden) :
    filtered

  const isHelpMode   = result?.type === 'help'
  const isPickerMode = result?.type === 'picker'
  const isCmdMode    = !result && hasTyped && !isProjectArgMode

  // Feedback line text
  const feedback = loading
    ? 'running…'
    : isProjectArgMode
      ? !projectsFetchedRef.current || projects.length === 0
        ? 'loading projects…'
        : projectSuggestions.length === 0 && argText
          ? 'no projects matching "' + argText + '"'
          : projectSuggestions.length === projects.length
            ? projectSuggestions.length + ' projects'
            : projectSuggestions.length + ' match' + (projectSuggestions.length === 1 ? '' : 'es')
      : result?.feedback
        ? result.feedback
        : !hasTyped
          ? 'type a command — try /help'
          : ''

  const feedbackIsError = result?.type === 'error'

  /* ── Open / close ───────────────────────────────────────────────────────────── */

  function openBar() {
    setOpen(true)
    setQuery('')
    setResult(null)
    setLoading(false)
    setSelectedIdx(0)
    setActiveCmdId(null)
    setHistIdx(-1)
    wf.commandOpen = true

    // Preload the project list the first time the bar is opened so /go autocomplete
    // is instant for the rest of the session.
    if (!projectsFetchedRef.current) {
      projectsFetchedRef.current = true
      getActiveProjects()
        .then(records => setProjects(records))
        .catch(() => {})   // silently degrade — /go will show "no projects" if it fails
    }
  }

  function closeBar(opts) {
    const keepFilter = !!(opts && opts.keepFilter)
    if (!keepFilter && wf.cmdFilter) {
      wf.cmdFilter = null
      window.dispatchEvent(new Event('outline-render'))
    }
    setOpen(false)
    setResult(null)
    setLoading(false)
    setHistIdx(-1)
    wf.commandOpen = false
    wf.commandQuery = ''
  }

  /* ── Event listeners ────────────────────────────────────────────────────────── */

  useEffect(() => {
    const onOpen = () => openBar()
    window.addEventListener('open-command-bar', onOpen)
    return () => window.removeEventListener('open-command-bar', onOpen)
  }, [])

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus()
  }, [open])

  /* ── Execution ──────────────────────────────────────────────────────────────── */

  async function runCommand(cmd, rawQuery) {
    const argsStart = rawQuery.indexOf(' ')
    const args      = argsStart >= 0 ? rawQuery.slice(argsStart + 1).trim() : ''

    setLoading(true)
    setResult(null)
    setActiveCmdId(cmd.id)
    setSelectedIdx(0)

    try {
      const res = await executeCommand(cmd.id, args, ctx)
      wf.commandHistory = [rawQuery, ...wf.commandHistory.filter(h => h !== rawQuery)].slice(0, 10)
      if (res.type === 'navigate') { closeBar(); return }
      // A picker needs a follow-up selection, /help is a reference table, and an error must stay
      // visible — every other type is a completed action, so the bar closes (keeping any filter the
      // command just applied so the outline reflects it).
      if (res.type === 'picker' || res.type === 'help' || res.type === 'error') {
        setResult(res)
        setSelectedIdx(0)
      } else {
        closeBar({ keepFilter: true })
      }
    } catch (err) {
      setResult({ type: 'error', feedback: 'Command failed: ' + (err?.message || String(err)) })
    } finally {
      setLoading(false)
    }
  }

  /* ── Navigate to a project and close ───────────────────────────────────────── */

  function navigateToProject(proj) {
    ctx.closeAllWorkspaces()
    ctx.zoomToProject(proj.projectId)
    closeBar()
  }

  /* ── Keyboard handler ───────────────────────────────────────────────────────── */

  function onKeyDown(e) {
    // Stop propagation for all keys we handle so outline.js's document-level
    // keydown listener never sees them while the command bar is open.
    if (e.key === 'Escape' || e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter') {
      e.stopPropagation()
    }

    if (e.key === 'Escape') {
      closeBar()
      return
    }

    const listLen = suggestions.length
    const hist = wf.commandHistory || []

    if (e.key === 'ArrowUp') {
      e.preventDefault()
      // History recall: when the input is empty, or we're already cycling history, step further back
      // (each press goes one command older). Otherwise move up through the filtered suggestion list.
      if ((histIdx >= 0 || !hasTyped) && hist.length) {
        const ni = Math.min((histIdx < 0 ? -1 : histIdx) + 1, hist.length - 1)
        setHistIdx(ni)
        setQuery(hist[ni]); wf.commandQuery = hist[ni]
        setSelectedIdx(0)
        return
      }
      setSelectedIdx(i => Math.max(i - 1, 0))
      return
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      // Cycling history → step toward more-recent commands, then back out to an empty prompt.
      if (histIdx >= 0) {
        const ni = histIdx - 1
        if (ni < 0) { setHistIdx(-1); setQuery(''); wf.commandQuery = '' }
        else { setHistIdx(ni); setQuery(hist[ni]); wf.commandQuery = hist[ni] }
        setSelectedIdx(0)
        return
      }
      setSelectedIdx(i => Math.min(i + 1, listLen - 1))
      return
    }

    if (e.key === 'Enter') {
      e.preventDefault()

      // Project-arg mode — navigate directly without calling the executor
      if (isProjectArgMode && projectSuggestions.length > 0) {
        const proj = projectSuggestions[selectedIdx] ?? projectSuggestions[0]
        if (proj) navigateToProject(proj)
        return
      }

      // Picker selection (executor returned a list)
      if (isPickerMode && result.items?.length) {
        const item = result.items[selectedIdx]
        if (item) {
          const next = selectPickerItem(activeCmdId, item, ctx)
          if (next?.type === 'navigate') { closeBar(); return }
          setResult(next)
          setSelectedIdx(0)
        }
        return
      }

      // Execute selected command from filtered list
      if (isCmdMode && filtered.length) {
        const cmd = filtered[selectedIdx] || filtered[0]
        if (cmd) runCommand(cmd, query)
        return
      }

      // Try exact trigger match (fallback for /go with no live project selected, etc.)
      if (!result && !isProjectArgMode) {
        const q   = normalised.split(' ')[0]
        const cmd = COMMANDS.find(c => c.trigger === q && isVisible(c))
        if (cmd) runCommand(cmd, query)
      }
    }
  }

  function onInputChange(e) {
    const val = e.target.value
    setQuery(val)
    wf.commandQuery = val
    // Clear any active filter when typing a new command
    if (wf.cmdFilter) {
      wf.cmdFilter = null
      window.dispatchEvent(new Event('outline-render'))
    }
    setResult(null)
    setSelectedIdx(0)
    setActiveCmdId(null)
    setHistIdx(-1)   // typing exits history-recall mode
  }

  /* ── Render ──────────────────────────────────────────────────────────────────── */

  const hasSuggestions = open && suggestions.length > 0

  return (
    <div className={`aj-cmd-bar${open ? ' aj-cmd-bar--open' : ''}`}>
      {open && (
        <>
          {/* Suggestions — grows bar upward */}
          {hasSuggestions && (
            <div className="aj-cmd-suggestions">
              {isHelpMode ? (
                // /help: render the full command table (not keyboard-selectable). hidden:true
                // commands (e.g. the secret /aj) are excluded so they stay out of the helper text.
                COMMANDS.filter(c => isVisible(c) && !c.hidden).map(cmd => (
                  <div key={cmd.id} className="aj-cmd-suggestion-row aj-cmd-suggestion-row--help">
                    <span className="aj-cmd-s-trigger">
                      {cmd.trigger}{cmd.args ? ' ' + cmd.args : ''}
                    </span>
                    <span className="aj-cmd-s-desc">{cmd.description}</span>
                  </div>
                ))
              ) : (
                suggestions.map((item, i) => {
                  const isCmd     = item.trigger !== undefined   // command vs project/picker item
                  const selected  = selectedIdx === i

                  let onClick
                  if (isProjectArgMode) {
                    onClick = () => navigateToProject(item)
                  } else if (isPickerMode) {
                    onClick = () => {
                      const next = selectPickerItem(activeCmdId, item, ctx)
                      if (next?.type === 'navigate') { closeBar(); return }
                      setResult(next)
                      setSelectedIdx(0)
                    }
                  } else if (isCmdMode) {
                    onClick = () => runCommand(item, query)
                  }

                  return (
                    <div
                      key={(item.id || item.trigger) + i}
                      className={`aj-cmd-suggestion-row${selected ? ' aj-cmd-suggestion-row--hi' : ''}`}
                      onClick={onClick}
                      onMouseEnter={() => setSelectedIdx(i)}
                    >
                      <span className="aj-cmd-s-trigger">
                        {isCmd
                          ? item.trigger + (item.args ? ' ' + item.args : '')
                          : item.label}
                      </span>
                      <span className="aj-cmd-s-desc">
                        {isCmd ? item.description : item.sublabel}
                      </span>
                    </div>
                  )
                })
              )}
            </div>
          )}

          {/* Divider between suggestions and feedback */}
          {hasSuggestions && <div className="aj-cmd-bar-divider" />}

          {/* Feedback line */}
          <div className={`aj-cmd-feedback${feedbackIsError ? ' aj-cmd-feedback--err' : ''}`}>
            {feedback || ' '}
          </div>

          {/* Input row */}
          <div className="aj-cmd-input-row">
            <span className="aj-cmd-prompt">/</span>
            <input
              ref={inputRef}
              type="text"
              className="aj-cmd-input"
              value={query.startsWith('/') ? query.slice(1) : query}
              onChange={e => onInputChange({
                target: { value: (e.target.value.startsWith('/') ? '' : '/') + e.target.value },
              })}
              onKeyDown={onKeyDown}
              placeholder="_"
              spellCheck={false}
              autoComplete="off"
              tabIndex={0}
            />
            <span className="aj-cmd-esc-hint" onClick={closeBar}><span className="aj-cmd-esc-key">esc</span><span className="aj-cmd-esc-touch">Close</span></span>
          </div>
        </>
      )}
    </div>
  )
}
